(function () {
  'use strict';

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,video[controls],[tabindex]:not([tabindex="-1"])';
  var swiperLoader = null;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-fpv2-swiper]');
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-fpv2-swiper', '');
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function loadSwiper(src) {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (!src) return Promise.resolve(null);
    if (!swiperLoader) {
      swiperLoader = loadScript(src)
        .then(function () {
          return window.Swiper || null;
        })
        .catch(function () {
          return null;
        });
    }
    return swiperLoader;
  }

  function readJSON(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      return null;
    }
  }

  function formatMoney(cents, format) {
    var value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    if (typeof value !== 'number' || isNaN(value)) return '';
    var pattern = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;

    function group(number, precision, thousands, decimal) {
      var fixed = (number / 100.0).toFixed(precision);
      var parts = fixed.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var tail = parts[1] ? decimal + parts[1] : '';
      return head + tail;
    }

    var match = pattern.match(placeholder);
    var name = match ? match[1] : 'amount';
    var output;

    switch (name) {
      case 'amount_no_decimals':
        output = group(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        output = group(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        output = group(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        output = group(value, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        output = group(value, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        output = group(value, 2, "'", '.');
        break;
      default:
        output = group(value, 2, ',', '.');
    }

    return pattern.replace(placeholder, output);
  }

  function toEmbedUrl(url) {
    if (!url) return '';
    var youtube = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
    if (youtube) {
      return 'https://www.youtube.com/embed/' + youtube[1] + '?autoplay=1&rel=0&playsinline=1';
    }
    var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) {
      return 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1';
    }
    return url;
  }

  function routeRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  class FeaturedProductV2 extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onExternalVariant = this.onExternalVariant.bind(this);
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = readJSON(this.querySelector('[data-fpv2-config]')) || {};
      this.variants = readJSON(this.querySelector('[data-fpv2-variants]')) || [];
      this.strings = this.config.strings || {};
      this.controller = new AbortController();
      this.activeModal = null;
      this.lastFocused = null;

      // Pristine gallery HTML capture, before any bundle swap happens
      var wrapperEl = this.querySelector('[data-fpv2-slider] .swiper-wrapper');
      this.defaultGalleryHTML = wrapperEl ? wrapperEl.innerHTML : '';

      var signal = this.controller.signal;
      this.addEventListener('click', this.onClick, { signal: signal });
      this.addEventListener('submit', this.onSubmit, { signal: signal });
      document.addEventListener('keydown', this.onKeydown, { signal: signal });

      ['variant:change', 'variant:changed', 'variant:update'].forEach(
        function (name) {
          document.documentElement.addEventListener(name, this.onExternalVariant, { signal: signal });
        }.bind(this)
      );

      this.initGallery();
      this.initUgc();
      this.syncVariant(this.initialVariantId(), false);

      // If a bundle option is already pre-selected on page load, sync the gallery
      this.syncInitialBundleSelection();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.gallerySwiper && this.gallerySwiper.destroy) this.gallerySwiper.destroy(true, true);
      if (this.ugcSwiper && this.ugcSwiper.destroy) this.ugcSwiper.destroy(true, true);
      this.gallerySwiper = null;
      this.ugcSwiper = null;
      this.releaseScroll();
      this.ready = false;
    }

    initialVariantId() {
      var input = this.querySelector('[data-fpv2-variant-input]');
      if (input && input.value) return input.value;
      if (this.variants.length) return this.variants[0].id;
      return null;
    }

    /**
     * Swaps the main gallery slides for the ones matching a Kaching bundle option.
     * bundleTitle is matched (trimmed, case-insensitive) against the
     * data-fpv2-bundle-media attribute rendered by each "Bundle gallery override" block.
     * Falls back to the original, pristine gallery when no match is found.
     */
    swapGalleryForBundle(bundleTitle) {
      var root = this.querySelector('[data-fpv2-slider]');
      var wrapper = root ? root.querySelector('.swiper-wrapper') : null;
      if (!wrapper) return;

      var normalized = (bundleTitle || '').trim().toLowerCase();
      var templates = this.querySelectorAll('[data-fpv2-bundle-media]');
      var match = null;
      for (var i = 0; i < templates.length; i += 1) {
        var key = (templates[i].getAttribute('data-fpv2-bundle-media') || '').trim().toLowerCase();
        if (key === normalized) {
          match = templates[i];
          break;
        }
      }

      var html = match ? match.innerHTML : this.defaultGalleryHTML;
      this.setGalleryHTML(html);
    }

    /**
     * Replaces the gallery slide markup and reinitializes Swiper against it.
     * Swiper caches slide references on init, so the instance must be destroyed
     * before the DOM changes and rebuilt afterwards.
     */
    setGalleryHTML(html) {
      var root = this.querySelector('[data-fpv2-slider]');
      var wrapper = root ? root.querySelector('.swiper-wrapper') : null;
      if (!wrapper) return;

      if (this.gallerySwiper && this.gallerySwiper.destroy) {
        this.gallerySwiper.destroy(true, true);
        this.gallerySwiper = null;
      }

      wrapper.innerHTML = html;
      this.initGallery();
    }

    /**
     * Handles the case where a bundle radio is already checked when the page loads
     * (e.g. restored form state), so the gallery matches on first paint too.
     */
    syncInitialBundleSelection() {
      var checked = document.querySelector('input[type="radio"][name^="kaching-bundles-deal-"]:checked');
      if (!checked) return;
      var label = document.querySelector('label[for="' + checked.id + '"]');
      if (!label) return;
      var titleEl = label.querySelector('.kaching-bundles__bar-title');
      if (!titleEl) return;
      this.swapGalleryForBundle(titleEl.textContent.trim());
    }

    initGallery() {
      var root = this.querySelector('[data-fpv2-slider]');
      if (!root) return;
      var options = this.config.gallery || {};
      var prev = this.querySelector('[data-fpv2-prev]');
      var next = this.querySelector('[data-fpv2-next]');
      var pagination = this.querySelector('[data-fpv2-pagination]');
      var slides = root.querySelectorAll('.swiper-slide');

      if (slides.length < 2) {
        if (prev) prev.hidden = true;
        if (next) next.hidden = true;
        if (pagination) pagination.hidden = true;
        return;
      }

      // Reset visibility in case a previous single-image bundle swap hid these
      if (prev) prev.hidden = false;
      if (next) next.hidden = false;
      if (pagination) pagination.hidden = false;

      loadSwiper(this.config.swiperUrl).then(
        function (Swiper) {
          if (!this.isConnected) return;
          if (!Swiper) {
            this.galleryFallback(root, prev, next);
            return;
          }
          var settings = {
            speed: options.speed || 500,
            loop: !!options.loop,
            spaceBetween: options.spaceBetween || 0,
            grabCursor: true,
            watchOverflow: true,
            a11y: { enabled: true },
            keyboard: { enabled: true, onlyInViewport: true }
          };
          if (options.autoplay) {
            settings.autoplay = { delay: options.autoplaySpeed || 5000, disableOnInteraction: false, pauseOnMouseEnter: true };
          }
          if (prev && next) {
            settings.navigation = { prevEl: prev, nextEl: next, disabledClass: 'swiper-button-disabled' };
          }
          if (pagination) {
            settings.pagination = {
              el: pagination,
              clickable: true,
              bulletClass: 'swiper-pagination-bullet',
              bulletActiveClass: 'swiper-pagination-bullet-active'
            };
          }
          this.gallerySwiper = new Swiper(root, settings);
        }.bind(this)
      );
    }

    galleryFallback(root, prev, next) {
      root.classList.add('featured-product-v2__slider--fallback');
      var track = root.querySelector('.swiper-wrapper');
      if (!track) return;
      var signal = this.controller.signal;
      var step = function (direction) {
        track.scrollBy({ left: track.clientWidth * direction, behavior: 'smooth' });
      };
      if (prev) prev.addEventListener('click', function () { step(-1); }, { signal: signal });
      if (next) next.addEventListener('click', function () { step(1); }, { signal: signal });
    }

    initUgc() {
      var root = this.querySelector('[data-fpv2-ugc]');
      if (!root) return;
      var options = this.config.ugc || {};
      loadSwiper(this.config.swiperUrl).then(
        function (Swiper) {
          if (!this.isConnected) return;
          if (!Swiper) {
            root.classList.add('featured-product-v2__ugc--fallback');
            return;
          }
          this.ugcSwiper = new Swiper(root, {
            slidesPerView: 'auto',
            spaceBetween: options.gap || 14,
            watchOverflow: true,
            freeMode: { enabled: true, sticky: false },
            grabCursor: true,
            a11y: { enabled: true }
          });
        }.bind(this)
      );
    }

    onClick(event) {
      var ugcTrigger = event.target.closest('[data-fpv2-ugc-open]');
      if (ugcTrigger && this.contains(ugcTrigger)) {
        event.preventDefault();
        this.openVideo(ugcTrigger);
        return;
      }

      var modalTrigger = event.target.closest('[data-fpv2-modal-open]');
      if (modalTrigger && this.contains(modalTrigger)) {
        event.preventDefault();
        var target = this.querySelector('[data-fpv2-modal="' + modalTrigger.getAttribute('data-fpv2-modal-open') + '"]');
        if (target) this.openModal(target);
        return;
      }

      var closer = event.target.closest('[data-fpv2-modal-close],[data-fpv2-modal-overlay]');
      if (closer && this.contains(closer)) {
        event.preventDefault();
        this.closeModal();
        return;
      }

      var adder = event.target.closest('[data-fpv2-add]');
      if (adder && this.contains(adder)) {
        event.preventDefault();
        var form = adder.closest('form');
        if (form) this.addToCart(form, adder, this.formBehaviour(form));
      }
    }

    formBehaviour(form) {
      var wrapper = form.closest('[data-fpv2-after-add]');
      if (wrapper) return wrapper.getAttribute('data-fpv2-after-add');
      return (this.config.cart && this.config.cart.afterAdd) || 'drawer';
    }

    formMode(form) {
      var wrapper = form.closest('[data-fpv2-form-mode]');
      if (wrapper) return wrapper.getAttribute('data-fpv2-form-mode');
      return (this.config.cart && this.config.cart.mode) || 'ajax';
    }

    onSubmit(event) {
      var form = event.target.closest('form');
      if (!form || !this.contains(form)) return;
      if (!form.classList.contains('featured-product-v2__form')) return;
      if (this.formMode(form) !== 'ajax') return;
      event.preventDefault();
      var button = form.querySelector('[data-fpv2-submit]') || form.querySelector('[type="submit"]');
      this.addToCart(form, button, this.formBehaviour(form));
    }

    onKeydown(event) {
      if (!this.activeModal) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(this.activeModal.querySelectorAll(FOCUSABLE), function (node) {
        return node.offsetParent !== null || node.getClientRects().length > 0;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onExternalVariant(event) {
      var detail = event.detail || {};
      var variant = detail.variant || detail;
      if (!variant || !variant.id) return;
      var known = this.variants.some(function (item) {
        return String(item.id) === String(variant.id);
      });
      if (!known) return;
      this.syncVariant(variant.id, true);
    }

    syncVariant(id, moveGallery) {
      if (!id) return;
      var variant = null;
      for (var i = 0; i < this.variants.length; i += 1) {
        if (String(this.variants[i].id) === String(id)) {
          variant = this.variants[i];
          break;
        }
      }
      if (!variant) return;
      this.currentVariant = variant;
      var format = this.config.moneyFormat;

      this.querySelectorAll('[data-fpv2-variant-input]').forEach(function (input) {
        input.value = variant.id;
      });

      this.querySelectorAll('[data-fpv2-price]').forEach(function (node) {
        node.textContent = formatMoney(variant.price, format);
      });

      this.querySelectorAll('[data-fpv2-compare]').forEach(function (node) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          node.textContent = formatMoney(variant.compare_at_price, format);
          node.hidden = false;
        } else {
          node.hidden = true;
        }
      });

      this.classList.toggle('featured-product-v2--stock-unavailable', !variant.available);

      var labels = this.strings;
      this.querySelectorAll('[data-fpv2-submit]').forEach(function (button) {
        button.disabled = !variant.available;
        var label = button.querySelector('[data-fpv2-button-label]');
        if (!label) return;
        var soldOut = button.getAttribute('data-fpv2-sold-out-label') || labels.soldOut;
        var available = button.getAttribute('data-fpv2-label') || label.textContent;
        label.textContent = variant.available ? available : soldOut;
      });

      this.renderStock(variant);

      if (moveGallery && variant.media_id) this.goToMedia(variant.media_id);
    }

    /**
     * FIX: delivery date ab poora DOM data-attributes se calculate hota hai,
     * this.config (jo sirf connectedCallback pe ek baar cache hota hai) se nahi.
     * Isse Theme Editor me setting change karte hi turant reflect hota hai,
     * chahe custom element reconnect ho ya na ho.
     */
    deliveryDate(node) {
      if (!node) return '';
      if (node.getAttribute('data-fpv2-delivery-enabled') !== 'true') return '';

      var daysNeeded = parseInt(node.getAttribute('data-fpv2-delivery-days'), 10) || 0;
      var businessOnly = node.getAttribute('data-fpv2-delivery-business-only') === 'true';
      var template = node.getAttribute('data-fpv2-delivery-template') || '{day} {month}';
      var months = (node.getAttribute('data-fpv2-delivery-months') || '').split(',').map(function (s) {
        return s.trim();
      });
      var weekdays = (node.getAttribute('data-fpv2-delivery-weekdays') || '').split(',').map(function (s) {
        return s.trim();
      });

      var date = new Date();
      var added = 0;

      if (daysNeeded > 0) {
        var safety = 0;
        while (added < daysNeeded && safety < 200) {
          date.setDate(date.getDate() + 1);
          safety += 1;
          var dow = date.getDay(); // 0 = Sunday, 6 = Saturday
          if (businessOnly && (dow === 0 || dow === 6)) continue;
          added += 1;
        }
      }

      var dateString = template
        .replace('{day}', String(date.getDate()))
        .replace('{month}', months[date.getMonth()] || '')
        .replace('{weekday}', weekdays[date.getDay()] || '');

      return '<strong>' + dateString + '</strong>';
    }

    renderStock(variant) {
      var node = this.querySelector('[data-fpv2-stock-text]');
      if (!node) return;
      var available = variant ? variant.available : true;
      var template = available
        ? node.getAttribute('data-fpv2-in-stock') || ''
        : node.getAttribute('data-fpv2-out-of-stock') || '';
      if (!template) return;
      node.innerHTML = template.replace('{delivery}', this.deliveryDate(node));
    }

    goToMedia(mediaId) {
      var slides = Array.prototype.slice.call(this.querySelectorAll('[data-fpv2-slider] .swiper-slide'));
      var index = -1;
      for (var i = 0; i < slides.length; i += 1) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          index = i;
          break;
        }
      }
      if (index < 0) return;
      if (this.gallerySwiper) {
        if (this.gallerySwiper.params.loop && this.gallerySwiper.slideToLoop) {
          this.gallerySwiper.slideToLoop(index);
        } else {
          this.gallerySwiper.slideTo(index);
        }
        return;
      }
      slides[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    openVideo(trigger) {
      var modal = this.querySelector('[data-fpv2-video-modal]');
      if (!modal) return;
      var stage = modal.querySelector('[data-fpv2-video-stage]');
      if (!stage) return;
      var source = trigger.getAttribute('data-fpv2-video-src');
      if (!source) return;
      var title = trigger.getAttribute('data-fpv2-video-title') || '';
      stage.innerHTML = '';

      if (trigger.getAttribute('data-fpv2-video-type') === 'external') {
        var frame = document.createElement('iframe');
        frame.src = toEmbedUrl(source);
        frame.title = title;
        frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        frame.setAttribute('allowfullscreen', '');
        stage.appendChild(frame);
      } else {
        var video = document.createElement('video');
        video.src = source;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        var poster = trigger.getAttribute('data-fpv2-video-poster');
        if (poster) video.poster = poster;
        stage.appendChild(video);
      }

      this.openModal(modal);
    }

    openModal(modal) {
      this.lastFocused = document.activeElement;
      modal.hidden = false;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      this.activeModal = modal;
      this.lockScroll();
      var target = modal.querySelector('[data-fpv2-modal-close]') || modal.querySelector(FOCUSABLE);
      if (target) target.focus();
    }

    closeModal() {
      var modal = this.activeModal;
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;
      var stage = modal.querySelector('[data-fpv2-video-stage]');
      if (stage) stage.innerHTML = '';
      this.activeModal = null;
      this.releaseScroll();
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
      this.lastFocused = null;
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.scrollLocked = true;
    }

    releaseScroll() {
      if (!this.scrollLocked) return;
      document.body.style.overflow = this.previousOverflow || '';
      this.scrollLocked = false;
    }

    announce(message) {
      var region = this.querySelector('[data-fpv2-status]');
      if (!region || !message) return;
      region.textContent = message;
    }

    async addToCart(form, button, behaviour) {
      if (!form || this.busy) return;
      this.busy = true;
      if (button) {
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
      }
      this.announce('');

      try {
        var response = await fetch(routeRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        var payload = await response.json();
        if (!response.ok) {
          throw new Error(payload.description || payload.message || this.strings.error || 'Error');
        }
        this.announce(this.strings.added || '');
        await this.afterAdd(payload, behaviour);
      } catch (error) {
        this.announce(error && error.message ? error.message : this.strings.error || '');
      } finally {
        this.busy = false;
        if (button) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-busy');
        }
      }
    }

    async afterAdd(item, behaviour) {
      var cart = null;
      try {
        cart = await fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
          return response.json();
        });
      } catch (error) {
        cart = null;
      }

      var root = document.documentElement;
      ['cart:refresh', 'cart:change', 'cart:added', 'variant:add'].forEach(function (name) {
        root.dispatchEvent(
          new CustomEvent(name, {
            bubbles: true,
            detail: { cart: cart, item: item, variant: item, quantity: item && item.quantity }
          })
        );
      });

      var mode = behaviour || (this.config.cart && this.config.cart.afterAdd) || 'drawer';
      if (mode === 'cart') {
        window.location.href = routeRoot() + 'cart';
        return;
      }
      if (mode === 'drawer') this.openCartDrawer();
    }

    openCartDrawer() {
      var settings = this.config.cart || {};
      var drawer = settings.drawerSelector ? document.querySelector(settings.drawerSelector) : null;

      if (drawer) {
        if (typeof drawer.show === 'function') {
          drawer.show();
          return true;
        }
        if (typeof drawer.open === 'function') {
          drawer.open();
          return true;
        }
        if (typeof drawer.showModal === 'function' && drawer.tagName === 'DIALOG') {
          drawer.showModal();
          return true;
        }
        drawer.setAttribute('open', '');
        drawer.setAttribute('aria-hidden', 'false');
        return true;
      }

      if (settings.openerSelector) {
        var opener = document.querySelector(settings.openerSelector);
        if (opener) {
          opener.click();
          return true;
        }
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      return false;
    }
  }

  if (!customElements.get('featured-product-v2')) {
    customElements.define('featured-product-v2', FeaturedProductV2);
  }

  document.addEventListener('change', function (event) {
    var radio = event.target.closest('input[type="radio"][name^="kaching-bundles-deal-"]');
    if (!radio) return;

    var label = document.querySelector('label[for="' + radio.id + '"]');
    if (!label) return;

    var titleEl = label.querySelector('.kaching-bundles__bar-title');
    if (!titleEl) return;

    var bundleTitle = titleEl.textContent.trim();
    var featured = document.querySelector('featured-product-v2');
    if (!featured || typeof featured.swapGalleryForBundle !== 'function') return;

    featured.swapGalleryForBundle(bundleTitle);
  });
})();